<script lang="ts">
  import { push, link } from "svelte-spa-router";
  import { onMount } from "svelte";
  import EditorForm from "../lib/components/EditorForm.svelte";
  import { entries, bySlug, hasStub, getStub } from "../lib/content";
  import { isSignedIn } from "../lib/adminStore";
  import { fromSop, serialize } from "../lib/serializeFrontmatter";
  import type { EditorState } from "../lib/serializeFrontmatter";
  import { putSop, deleteSop } from "../lib/api";
  import { applyOverride, dropOverride } from "../lib/runtime";
  import { parseFrontmatter } from "../lib/frontmatter";
  import type { Sop } from "../lib/types";

  export let params: { slug: string } = { slug: "" };

  let state: EditorState | null = null;
  let saving = false;
  let error: string | null = null;
  let stubExists = false;

  $: sop = bySlug($entries, params.slug);

  // Initialise editor state once we have a candidate SOP. Re-run if the slug
  // changes or the underlying entry shape gains a value (e.g. overrides land).
  let lastSlug = "";
  $: if (sop && (state === null || lastSlug !== params.slug)) {
    state = fromSop(sop);
    stubExists = hasStub(params.slug);
    lastSlug = params.slug;
  }

  onMount(() => {
    if (!$isSignedIn) push("/admin");
  });

  async function save() {
    if (!state) return;
    error = null;
    // Bump updated date on save so the changelog reflects this change.
    state.updated = new Date().toISOString().slice(0, 10);
    const md = serialize(state);
    saving = true;
    try {
      await putSop(state.slug, md);
      applyOverride(state.slug, md);
      push(`/sop/${state.slug}`);
    } catch (e) {
      error = (e as Error).message;
    } finally {
      saving = false;
    }
  }

  async function revert() {
    if (!sop) return;
    if (!stubExists) {
      // No stub means deleting the override removes the SOP entirely.
      const ok = window.confirm(
        `This SOP has no on-disk stub. Deleting will remove it permanently. Continue?`,
      );
      if (!ok) return;
    } else {
      const ok = window.confirm(
        `Revert this SOP to its on-disk stub? Your override will be deleted from KV.`,
      );
      if (!ok) return;
    }
    saving = true;
    error = null;
    try {
      await deleteSop(sop.slug);
      dropOverride(sop.slug);
      push(stubExists ? `/sop/${sop.slug}` : "/admin");
    } catch (e) {
      error = (e as Error).message;
    } finally {
      saving = false;
    }
  }

  function cancel() {
    if (sop) push(`/sop/${sop.slug}`);
    else push("/admin");
  }
</script>

{#if !$isSignedIn}
  <div class="panel p-8 text-center">
    <p class="text-text-muted mb-4">Sign in to edit.</p>
    <a use:link href="/admin" class="btn-primary">Go to admin</a>
  </div>
{:else if !sop || !state}
  <div class="panel p-8 text-center text-text-muted">No SOP at that slug.</div>
{:else}
  <div class="flex flex-col gap-4">
    <div class="flex items-center gap-2 text-sm text-text-muted">
      <a use:link href="/admin" class="hover:text-text">Admin</a>
      <span>/</span>
      <span>Edit</span>
    </div>
    <h1 class="text-2xl font-semibold tracking-tight">Edit · {state.title || params.slug}</h1>
    <p class="text-xs text-text-dim">
      {#if stubExists}
        Saving creates a live override on top of the on-disk stub. You can revert anytime.
      {:else}
        This SOP lives only in KV. Deleting the override removes it entirely.
      {/if}
    </p>

    <EditorForm
      bind:state
      saving={saving}
      error={error}
      onsubmit={save}
      oncancel={cancel}
      ondelete={revert}
    />
  </div>
{/if}
